import React, { useEffect, useState } from 'react';
import '../../styles/user/bgvCompletedCandidates.css';
import { fetchBgvCompletedCandidates } from '../../services/user/api';
import { Search } from 'lucide-react';
import { FaFileAlt } from 'react-icons/fa';
import { useNavigate } from "react-router-dom";

const BgvCompletedCandidatesPage = ({ onViewFinalReport }) => {

  const [candidates, setCandidates] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const navigate = useNavigate();
  const [sortField, setSortField] = useState('createddate');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    loadCandidates();
  }, [currentPage, entriesPerPage, search, sortField, sortOrder]);

  const loadCandidates = async () => {
    try {
      setIsLoading(true);
      const response = await fetchBgvCompletedCandidates(
        currentPage,
        entriesPerPage,
        search,
        sortField,
        sortOrder
      );

      if (response?.data?.status) {
        const data = response.data.data;
        setCandidates(data.content || []);
        setTotalElements(data.totalElements || 0);
        setFetchError("");
      } else {
        setCandidates([]);
        setTotalElements(0);
        setFetchError(response?.data?.message || 'Failed to fetch data.');
      }
    } catch (error) {
      setCandidates([]);
      setTotalElements(0);
      setFetchError("An error occurred while fetching data.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setCurrentPage(0);
  };

  const renderSortIcon = (field) => {
  return (
    <span className="sort-icons">
      <span
        className={`arrow ${
          sortField === field && sortOrder === 'asc' ? 'active' : ''
        }`}
      >
        ↑
      </span>
      <span
        className={`arrow ${
          sortField === field && sortOrder === 'desc' ? 'active' : ''
        }`}
      >
        ↓
      </span>
    </span>
  );
};


  const totalPages = Math.ceil(totalElements / entriesPerPage);

  return (
    <>
      <p className="client-header">BGV Completed Candidates</p>
      <div className="client-wrapper">
        {fetchError && <div className="error-message">{fetchError}</div>}

        <div className="clinet-table-controls">
          <div className="entries-select">
            Show{' '}
            <select
              value={entriesPerPage}
              onChange={(e) => {
                setEntriesPerPage(Number(e.target.value));
                setCurrentPage(0);
              }}
            >
              <option value="10">10</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>{' '}
            entries
          </div>

          <div className="client-search-box">
            <div className="client-search-input-wrapper">
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(0);
                }}
                placeholder="Search by any field..."
              />
              <Search className="client-search-icon" size={18} />
            </div>
          </div>
        </div>

        <div className="clinet-table-container">
          <table className="candidates-table">
            <colgroup>
              <col style={{ width: "140px" }} />
              <col style={{ width: "200px" }} />
              <col style={{ width: "240px" }} />
              <col style={{ width: "150px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "160px" }} />
              <col style={{ width: "200px" }} />
              <col style={{ width: "120px" }} />
            </colgroup>
            <thead>
              <tr>
                <th onClick={() => handleSort('candidateid')}>
                  Candidate ID{renderSortIcon('candidateid')}
                </th>
                <th onClick={() => handleSort('fullname')}>
                  Name{renderSortIcon('fullname')}
                </th>
                <th onClick={() => handleSort('email')}>
                  Email{renderSortIcon('email')}
                </th>
                <th onClick={() => handleSort('mobileNumber')}>
                  Mobile{renderSortIcon('mobileNumber')}
                </th>
                <th onClick={() => handleSort('dateOfBirth')}>
                  DOB{renderSortIcon('dateOfBirth')}
                </th>
                <th onClick={() => handleSort('caseInitiatedDateOnlyAsString')}>
                  Created Date{renderSortIcon('caseInitiatedDateOnlyAsString')}
                </th>
                <th onClick={() => handleSort('reportDateOnlyString')}>
                  BGV Completed Date {renderSortIcon('reportDateOnlyString')}
                </th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="8" className="loading">Loading Candidates...</td>
                </tr>
              ) : candidates.length > 0 ? (
                candidates.map((candidate) => (
                  <tr key={candidate.candidateid}>
                    <td>{candidate.candidateid}</td>
                    <td>{candidate.fullname}</td>
                    <td>{candidate.email}</td>
                    <td>{candidate.mobileNumber}</td>
                    <td>{candidate.dateOfBirth}</td>
                    <td>{candidate.caseInitiatedDateOnlyAsString || 'N/A'}</td>
                    <td>{candidate.reportDateOnlyString || 'N/A'}</td>
                    <td>
                      <button
                        className="icon-btn"
                        onClick={() => navigate(`/client/finalreport/${candidate.candidateid}`)}
                        disabled={candidate.bgcStatus !== 'COMPLETED'}
                        title="Download PDF"
                      >
                        <FaFileAlt
                          className={
                            candidate.bgcStatus === 'COMPLETED'
                              ? 'text-purple-600'
                              : 'text-gray-400'
                          }
                        />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="no-data">No matching candidates found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalElements > 0 && (
          <div className="pagination">
            <div className="pagination-summary">
              Showing {currentPage * entriesPerPage + 1} to{' '}
              {Math.min((currentPage + 1) * entriesPerPage, totalElements)} of{' '}
              {totalElements} entries
            </div>

            <div className="pagination-buttons-user">
              <button
                className="page-btn"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                disabled={currentPage === 0}
              >
                ←
              </button>

              <button
                className={`page-btn active`}
                onClick={() => setCurrentPage(currentPage)}
              >
                {currentPage + 1}
              </button>

              {currentPage + 1 < totalPages && (
                <button
                  className="page-btn"
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  {currentPage + 2}
                </button>
              )}

              <button
                className="page-btn"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))}
                disabled={currentPage === totalPages - 1}
              >
                →
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BgvCompletedCandidatesPage;